import type { BalanceSnapshot, MinorAmount } from "./BalanceTypes";

export class BalanceStore {
  private balanceMinor: MinorAmount;
  private listeners: Set<(snapshot: BalanceSnapshot) => void> = new Set();

  public constructor(initialBalanceMinor: MinorAmount) {
    this.balanceMinor = initialBalanceMinor;
  }

  public getSnapshot(): BalanceSnapshot {
    return { balanceMinor: this.balanceMinor };
  }

  public setBalanceMinor(nextMinor: MinorAmount): void {
    // minimal validation: must be an integer string
    if (!/^\d+$/.test(String(nextMinor))) {
      throw new Error(`BalanceStore.setBalanceMinor: invalid minor amount "${String(nextMinor)}" (expected integer string).`);
    }

    if (nextMinor === this.balanceMinor) return;

    this.balanceMinor = nextMinor;
    this.emit();
  }

  public deductMinorBetFromBalance(betMinor: MinorAmount): void {
    const betStr = String(betMinor);

    // minimal validation: must be an integer string
    if (!/^\d+$/.test(betStr)) {
      throw new Error(
        `BalanceStore.deductMinorBetFromBalance: invalid minor amount "${betStr}" (expected integer string).`
      );
    }

    const current = String(this.balanceMinor ?? "0");
    if (!/^\d+$/.test(current)) {
      throw new Error(
        `BalanceStore.deductMinorBetFromBalance: invalid current balance "${current}" (expected integer string).`
      );
    }

    const before = BigInt(current);
    const bet = BigInt(betStr);

    if (bet >= before) {
      this.balanceMinor = "0";
      this.emit();
      return;
    }
    
    this.balanceMinor = (before - bet).toString();
    this.emit();
  }

  public subscribe(listener: (snapshot: BalanceSnapshot) => void): () => void {
    this.listeners.add(listener);
    // immediate push
    listener(this.getSnapshot());

    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(): void {
    const snap = this.getSnapshot();
    for (const l of this.listeners) l(snap);
  }
}